// =============================================================================
// features/conversations/components/QualificationRuler.tsx — Régua de
// qualificação da IA no ContactPanel (F37-S05, doc 28 §2.1/§5/§6, plano
// docs/planejamento-briefing-handoff-regua-qualificacao.md §6).
//
// Timeline vertical de 9 passos mostrando até onde a Ana Clara chegou com o
// cliente — antes, durante e depois do handoff — sem o atendente precisar
// ler a conversa inteira. Componente pensado para reuso futuro (Kanban/CRM,
// doc 28 §9), mas só entra no live chat neste ciclo.
//
// Gate (D5, doc 28 §3): flag `livechat.ai_briefing.enabled` E permissão
// `livechat:conversation:read` — sem os dois, retorna `null` (ausente do
// DOM, nunca escondido só por CSS).
//
// Dados: GET /api/conversations/:id/ai-qualification (F37-S02) — 100%
// determinístico no backend, ZERO chamada a LLM aqui. Invalidado por
// `conversation:updated` via useConversationSocket (escopo 'detail').
//
// DS (doc 18): tokens de cor/espaçamento/elevação — zero hex hardcoded.
// Estado por token: done=--success, current=--brand-azul (pulso sutil),
// pending=--text-3, skipped=tracejado + --text-3 + label "pulou".
//
// LGPD (doc 17, doc 28 §3 D6): `value` de cada etapa nunca contém CPF,
// telefone ou renda bruta (garantido pelo backend) — este componente só
// exibe, nunca loga.
// =============================================================================

import * as React from 'react';

import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useAuth } from '../../../lib/auth-store';
import { formatOrgDateTimeShort } from '../../../lib/orgDateTime';
import { useAiQualification } from '../queries';
import type {
  ConversationBriefingRef,
  QualificationResponse,
  QualificationStepStatus,
} from '../types';

const FLAG_KEY = 'livechat.ai_briefing.enabled';
const READ_PERMISSION = 'livechat:conversation:read';
const COLLAPSE_STORAGE_KEY = 'elemento:livechat:qualification-ruler:collapsed';

// ---------------------------------------------------------------------------
// Helpers puros — testados isoladamente em __tests__/QualificationRuler.test.ts
// (convenção local: sem jsdom/RTL neste projeto — ver ContactPanel.test.tsx).
// ---------------------------------------------------------------------------

/** Lê a preferência de colapso. Storage injetável para teste puro sem jsdom. */
export function readCollapsedPref(storage: Pick<Storage, 'getItem'> | undefined): boolean {
  if (!storage) return false;
  try {
    return storage.getItem(COLLAPSE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Persiste a preferência de colapso. Nunca lança (modo privado / quota). */
export function writeCollapsedPref(
  storage: Pick<Storage, 'setItem'> | undefined,
  collapsed: boolean,
): void {
  if (!storage) return;
  try {
    storage.setItem(COLLAPSE_STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    // localStorage indisponível — colapsar não persiste, sem crash.
  }
}

export interface StepVisual {
  readonly colorVar: string;
  readonly dashed: boolean;
  readonly iconKind: 'check' | 'pulse' | 'dot' | 'skip';
}

/** Mapeia o estado de uma etapa (doc 28 §2.1) para o token visual do doc 18. */
export function resolveStepVisual(status: QualificationStepStatus): StepVisual {
  switch (status) {
    case 'done':
      return { colorVar: 'var(--success)', dashed: false, iconKind: 'check' };
    case 'current':
      return { colorVar: 'var(--brand-azul)', dashed: false, iconKind: 'pulse' };
    case 'skipped':
      return { colorVar: 'var(--text-3)', dashed: true, iconKind: 'skip' };
    case 'pending':
    default:
      return { colorVar: 'var(--text-3)', dashed: false, iconKind: 'dot' };
  }
}

export function progressLabel(completed: number, total: number): string {
  return `${completed.toString()} de ${total.toString()}`;
}

export function progressPercent(completed: number, total: number): number {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((completed / total) * 100)));
}

/**
 * A etapa `handoff.value` chega como `"<reasonLabel> · <isoDate>"` ou, sem
 * motivo conhecido, só o `isoDate` (ver `formatHandoff` em
 * apps/api/src/modules/livechat/qualification.service.ts — fonte real do
 * shape, não inventado aqui).
 */
export function parseHandoffValue(value: string | null): {
  readonly reasonLabel: string | null;
  readonly at: Date | null;
} {
  if (!value) return { reasonLabel: null, at: null };
  const sepIndex = value.lastIndexOf(' · ');
  if (sepIndex === -1) {
    const at = new Date(value);
    return { reasonLabel: null, at: Number.isNaN(at.getTime()) ? null : at };
  }
  const reasonLabel = value.slice(0, sepIndex);
  const isoPart = value.slice(sepIndex + 3);
  const at = new Date(isoPart);
  return { reasonLabel, at: Number.isNaN(at.getTime()) ? null : at };
}

/** Enums curtos do grafo (`ai-handoff.ts`) → rótulo legível em pt-BR. */
const HANDOFF_REASON_LABELS: Readonly<Record<string, string>> = {
  ai_requested: 'a IA encaminhou',
  ai_unavailable: 'IA indisponível',
  cliente_solicitou_atendente: 'cliente pediu atendente',
};

export function humanizeHandoffReason(reasonLabel: string | null): string | null {
  if (!reasonLabel) return null;
  return (
    HANDOFF_REASON_LABELS[reasonLabel] ??
    reasonLabel.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
  );
}

/** "há X min" / "há X h" / "há X d" — granularidade decrescente, sem libs externas. */
export function formatRelativeTime(at: Date, now: Date): string {
  const diffMs = now.getTime() - at.getTime();
  const diffMin = Math.max(0, Math.round(diffMs / 60_000));
  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `há ${diffMin.toString()} min`;
  const diffHours = Math.round(diffMin / 60);
  if (diffHours < 24) return `há ${diffHours.toString()} h`;
  const diffDays = Math.round(diffHours / 24);
  return `há ${diffDays.toString()} d`;
}

/** Formata um instante ISO no fuso da organização (nunca fuso do navegador). */
export function formatOrgDateTime(at: Date): string {
  return formatOrgDateTimeShort(at);
}

export type FooterState =
  | { readonly kind: 'ai_active' }
  | {
      readonly kind: 'handoff';
      readonly reasonLabel: string | null;
      readonly relative: string;
      readonly absolute: string;
    }
  | { readonly kind: 'idle' };

/** Deriva o rodapé contextual (plano §2.1) a partir da resposta da régua. */
export function resolveFooterState(data: QualificationResponse, now: Date): FooterState {
  if (data.aiActive) return { kind: 'ai_active' };

  const handoffStep = data.steps.find((s) => s.key === 'handoff');
  if (handoffStep && handoffStep.status === 'done' && handoffStep.value) {
    const { reasonLabel, at } = parseHandoffValue(handoffStep.value);
    if (at) {
      return {
        kind: 'handoff',
        reasonLabel: humanizeHandoffReason(reasonLabel),
        relative: formatRelativeTime(at, now),
        absolute: formatOrgDateTime(at),
      };
    }
  }
  return { kind: 'idle' };
}

// ---------------------------------------------------------------------------
// Sub-componentes visuais
// ---------------------------------------------------------------------------

function CheckIcon(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: 10, height: 10 }}
      aria-hidden="true"
    >
      <path d="M3 8.5l3 3 7-7" />
    </svg>
  );
}

function StepIcon({ visual }: { visual: StepVisual }): React.JSX.Element {
  const size = 20;
  const baseStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: 'var(--elev-1)',
  };

  if (visual.iconKind === 'check') {
    return (
      <span
        aria-hidden="true"
        style={{
          ...baseStyle,
          background: 'var(--success)',
          color: 'var(--text-on-brand)',
        }}
      >
        <CheckIcon />
      </span>
    );
  }

  if (visual.iconKind === 'pulse') {
    return (
      <span
        aria-hidden="true"
        className="animate-pulse"
        style={{
          ...baseStyle,
          background: 'color-mix(in srgb, var(--brand-azul) 18%, var(--bg-elev-1))',
          border: `2px solid ${visual.colorVar}`,
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: visual.colorVar,
          }}
        />
      </span>
    );
  }

  if (visual.iconKind === 'skip') {
    return (
      <span
        aria-hidden="true"
        style={{
          ...baseStyle,
          background: 'transparent',
          border: `1.5px dashed ${visual.colorVar}`,
          boxShadow: 'none',
        }}
      />
    );
  }

  // pending
  return (
    <span
      aria-hidden="true"
      style={{
        ...baseStyle,
        background: 'var(--bg-inset)',
        border: '1.5px solid var(--border-subtle)',
        boxShadow: 'none',
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: visual.colorVar,
        }}
      />
    </span>
  );
}

/** Um item da timeline (`<li>` semântico, `aria-current="step"` na etapa ativa). */
function StepItem({
  step,
  isLast,
}: {
  step: QualificationResponse['steps'][number];
  isLast: boolean;
}): React.JSX.Element {
  const visual = resolveStepVisual(step.status);

  return (
    <li
      aria-current={step.status === 'current' ? 'step' : undefined}
      style={{
        display: 'flex',
        gap: 10,
        position: 'relative',
        paddingBottom: isLast ? 0 : 14,
      }}
    >
      {/* Conector vertical entre passos */}
      {!isLast && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 9,
            top: 20,
            bottom: 0,
            width: 2,
            background:
              step.status === 'done'
                ? 'color-mix(in srgb, var(--success) 40%, transparent)'
                : 'var(--border-subtle)',
          }}
        />
      )}
      <StepIcon visual={visual} />
      <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
        <p
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-xs)',
            fontWeight: step.status === 'current' ? 600 : 500,
            color: step.status === 'pending' ? 'var(--text-3)' : 'var(--text)',
            margin: 0,
            lineHeight: 1.3,
          }}
        >
          {step.label}
          {step.status === 'skipped' && (
            <span
              style={{
                marginLeft: 6,
                fontSize: 10,
                fontWeight: 600,
                color: 'var(--text-3)',
                fontStyle: 'italic',
              }}
            >
              pulou
            </span>
          )}
        </p>
        {step.value && (
          <p
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              color: 'var(--text-3)',
              margin: '2px 0 0',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {step.value}
          </p>
        )}
      </div>
    </li>
  );
}

/** Barra de progresso — token de marca, nunca cor ad-hoc. */
function ProgressBar({ percent }: { percent: number }): React.JSX.Element {
  return (
    <div
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{
        height: 5,
        borderRadius: 999,
        background: 'var(--surface-muted)',
        overflow: 'hidden',
        boxShadow: 'inset 0 1px 2px var(--border-inner-dark)',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${percent.toString()}%`,
          borderRadius: 999,
          background: 'var(--grad-azul)',
          transition: `width var(--dur) var(--ease-out)`,
        }}
      />
    </div>
  );
}

function ChevronIcon({ collapsed }: { collapsed: boolean }): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        width: 14,
        height: 14,
        transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
        transition: `transform var(--dur-fast) var(--ease-out)`,
      }}
      aria-hidden="true"
    >
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

function Skeleton(): React.JSX.Element {
  return (
    <div
      style={{
        padding: '16px',
        borderBottom: '1px solid var(--border-subtle)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
      aria-hidden="true"
    >
      <div
        style={{ height: 10, width: '55%', borderRadius: 4, background: 'var(--surface-muted)' }}
      />
      <div
        style={{ height: 5, width: '100%', borderRadius: 999, background: 'var(--surface-muted)' }}
      />
      {[1, 2, 3].map((n) => (
        <div key={n} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: 'var(--surface-muted)',
            }}
          />
          <div
            style={{
              height: 9,
              width: `${(70 - n * 10).toString()}%`,
              borderRadius: 4,
              background: 'var(--surface-muted)',
            }}
          />
        </div>
      ))}
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  return (
    <div
      style={{
        padding: '16px',
        borderBottom: '1px solid var(--border-subtle)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        alignItems: 'flex-start',
      }}
    >
      <p
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-xs)',
          color: 'var(--danger)',
          margin: 0,
        }}
      >
        Não foi possível carregar a régua de qualificação.
      </p>
      <button
        type="button"
        onClick={onRetry}
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-xs)',
          color: 'var(--brand-azul)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textDecoration: 'underline',
          padding: 0,
        }}
      >
        Tentar novamente
      </button>
    </div>
  );
}

/** Rodapé contextual: IA ainda ativa, ou motivo/quando do handoff + botão do briefing. */
function Footer({
  footer,
  briefing,
  onOpenBriefing,
}: {
  footer: FooterState;
  briefing: ConversationBriefingRef | null | undefined;
  onOpenBriefing?: (() => void) | undefined;
}): React.JSX.Element | null {
  if (footer.kind === 'idle') return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
      {footer.kind === 'ai_active' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            aria-hidden="true"
            className="animate-pulse"
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--brand-verde)',
            }}
          />
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-xs)',
              color: 'var(--text-2)',
              margin: 0,
            }}
          >
            Ana Clara em atendimento
          </p>
        </div>
      )}

      {footer.kind === 'handoff' && (
        <p
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-xs)',
            color: 'var(--text-2)',
            margin: 0,
          }}
          title={footer.absolute}
        >
          Transferido{footer.reasonLabel ? ` · ${footer.reasonLabel}` : ''} · {footer.relative}
        </p>
      )}

      {briefing !== null && briefing !== undefined && (
        <button
          type="button"
          onClick={onOpenBriefing}
          disabled={onOpenBriefing === undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '7px 12px',
            borderRadius: 'var(--radius-sm)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-xs)',
            fontWeight: 600,
            color: 'var(--brand-azul)',
            background: 'color-mix(in srgb, var(--brand-azul) 8%, transparent)',
            border: '1px solid color-mix(in srgb, var(--brand-azul) 24%, transparent)',
            cursor: onOpenBriefing === undefined ? 'not-allowed' : 'pointer',
            opacity: onOpenBriefing === undefined ? 0.5 : 1,
            transition: `all var(--dur-fast) var(--ease-out)`,
            outline: 'none',
          }}
          onMouseEnter={(e) => {
            if (onOpenBriefing === undefined) return;
            (e.currentTarget as HTMLButtonElement).style.boxShadow = 'var(--glow-azul)';
            (e.currentTarget as HTMLButtonElement).style.borderColor = 'transparent';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
            (e.currentTarget as HTMLButtonElement).style.borderColor =
              'color-mix(in srgb, var(--brand-azul) 24%, transparent)';
          }}
          onFocus={(e) => {
            (e.currentTarget as HTMLButtonElement).style.boxShadow =
              '0 0 0 2px color-mix(in srgb, var(--brand-azul) 35%, transparent)';
          }}
          onBlur={(e) => {
            (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
          }}
        >
          Ver briefing da IA
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export interface QualificationRulerProps {
  readonly conversationId: string;
  /** Referência leve ao briefing (vem de `ConversationDetail.briefing`). */
  readonly briefing?: ConversationBriefingRef | null;
  /** Abre o modal do briefing (F37-S06). Ausente = botão desabilitado por ora. */
  readonly onOpenBriefing?: () => void;
}

/**
 * QualificationRuler — timeline vertical de 9 passos no ContactPanel.
 *
 * Gate: flag `livechat.ai_briefing.enabled` + permissão
 * `livechat:conversation:read`. Sem os dois, retorna `null` — nenhum vestígio
 * no DOM (DoD do slot).
 */
export function QualificationRuler({
  conversationId,
  briefing,
  onOpenBriefing,
}: QualificationRulerProps): React.JSX.Element | null {
  const { enabled: flagEnabled, isLoading: flagLoading } = useFeatureFlag(FLAG_KEY);
  const { hasPermission } = useAuth();
  const canRead = hasPermission(READ_PERMISSION);

  const [collapsed, setCollapsed] = React.useState<boolean>(() =>
    readCollapsedPref(typeof window === 'undefined' ? undefined : window.localStorage),
  );

  function toggleCollapsed(): void {
    setCollapsed((prev) => {
      const next = !prev;
      writeCollapsedPref(typeof window === 'undefined' ? undefined : window.localStorage, next);
      return next;
    });
  }

  const gated = flagEnabled && canRead;

  const { data, isLoading, isError, refetch } = useAiQualification(gated ? conversationId : '');

  // Sem o par flag+permissão: componente ausente do DOM (nunca escondido por
  // CSS). `flagLoading` evita um flash "sumido→aparece" no primeiro render.
  if (flagLoading) return null;
  if (!gated) return null;

  return (
    <div
      style={{
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      {/* ── Cabeçalho: título + "X de 9" + colapsar ─────────────────────── */}
      <button
        type="button"
        onClick={toggleCollapsed}
        aria-expanded={!collapsed}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '16px 16px 0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 10,
            fontWeight: 600,
            color: 'var(--text-3)',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          Qualificação pela IA
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {data && (
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--text-2)',
              }}
            >
              {progressLabel(data.completed, data.total)}
            </span>
          )}
          <span style={{ color: 'var(--text-3)' }}>
            <ChevronIcon collapsed={collapsed} />
          </span>
        </span>
      </button>

      {isLoading && <Skeleton />}

      {!isLoading && isError && (
        <div style={{ padding: '12px 16px 16px' }}>
          <ErrorState onRetry={() => void refetch()} />
        </div>
      )}

      {!isLoading && !isError && data && (
        <div style={{ padding: '10px 16px 16px' }}>
          <ProgressBar percent={progressPercent(data.completed, data.total)} />

          {!collapsed && (
            <>
              <ol
                aria-label="Etapas da qualificação pela IA"
                style={{ listStyle: 'none', margin: '14px 0 0', padding: 0 }}
              >
                {data.steps.map((step, idx) => (
                  <StepItem key={step.key} step={step} isLast={idx === data.steps.length - 1} />
                ))}
              </ol>

              <Footer
                footer={resolveFooterState(data, new Date())}
                briefing={briefing}
                onOpenBriefing={onOpenBriefing}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
